import { ViewPlugin, Decoration, EditorView } from '@codemirror/view';
import { StateField, StateEffect } from '@codemirror/state';
import { commentAnchorService } from '../../services/commentAnchorService';

// State effect for updating decorations
const updateAnchorsEffect = StateEffect.define();

// State field to track comment anchors
const commentAnchorsField = StateField.define({
    create() {
        return Decoration.none;
    },
    
    update(decorations, tr) {
        decorations = decorations.map(tr.changes);
        
        for (let effect of tr.effects) {
            if (effect.is(updateAnchorsEffect)) {
                decorations = createDecorations(tr.state);
            }
        }
        
        return decorations;
    },
    
    provide: field => EditorView.decorations.from(field)
});

// Create decorations from anchors
function createDecorations(state) {
    const anchors = commentAnchorService.getAnchorsForDocument('claims');
    const decorations = [];
    
    anchors.forEach(anchor => {
        if (anchor.isValid && anchor.startOffset < state.doc.length) {
            try {
                const from = Math.min(anchor.startOffset, state.doc.length);
                const to = Math.min(anchor.endOffset, state.doc.length);
                
                if (from < to) {
                    const className = commentAnchorService.getDecorationClass(anchor.id);
                    decorations.push(
                        Decoration.mark({
                            class: className,
                            attributes: {
                                'data-anchor-id': anchor.id,
                                'data-comment-id': anchor.id.replace('-anchor', '')
                            }
                        }).range(from, to)
                    );
                }
            } catch (error) {
                console.error('Error creating decoration for anchor:', anchor.id, error);
            }
        }
    });
    
    return Decoration.set(decorations, true);
}

// View plugin for handling clicks and updates
const commentAnchorPlugin = ViewPlugin.fromClass(class {
    constructor(view) {
        this.view = view;
        this.onCommentClick = null;
        
        // Initial reconciliation
        commentAnchorService.reconcileClaimsAnchors(view);
        
        // Set up click handler
        this.clickHandler = this.handleClick.bind(this);
        view.dom.addEventListener('click', this.clickHandler);
    }
    
    update(update) {
        if (update.docChanged) {
            // Reconcile anchors when document changes
            commentAnchorService.reconcileClaimsAnchors(update.view);
            
            // Schedule decoration update for next tick to avoid update-in-progress error
            Promise.resolve().then(() => {
                update.view.dispatch({
                    effects: updateAnchorsEffect.of(null)
                });
            });
        }
    }
    
    handleClick(event) {
        const target = event.target;
        const anchorElement = target.closest('[data-anchor-id]');
        
        if (anchorElement) {
            const anchorId = anchorElement.getAttribute('data-anchor-id');
            const commentId = anchorElement.getAttribute('data-comment-id');
            
            if (this.onCommentClick) {
                this.onCommentClick(commentId, anchorId);
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }
    
    destroy() {
        this.view.dom.removeEventListener('click', this.clickHandler);
    }
});

// Command functions
export function setCommentAnchor(view, commentId) {
    const anchor = commentAnchorService.createClaimsAnchor(view, commentId);
    if (anchor) {
        // Force decoration update
        view.dispatch({
            effects: updateAnchorsEffect.of(null)
        });
        return true;
    }
    return false;
}

export function removeCommentAnchor(view, anchorId) {
    commentAnchorService.removeAnchor(anchorId);
    // Force decoration update
    view.dispatch({
        effects: updateAnchorsEffect.of(null)
    });
}

export function navigateToCommentAnchor(view, anchorId) {
    return commentAnchorService.navigateToAnchor(anchorId, view);
}

// Extension factory
export function commentAnchor(options = {}) {
    const plugin = commentAnchorPlugin;
    
    // Set options on the plugin instance
    if (options.onCommentClick) {
        plugin.onCommentClick = options.onCommentClick;
    }
    
    return [
        commentAnchorsField,
        plugin
    ];
}